import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Alert, Typography } from '@mui/material';

interface ContractorRouteProps {
  children: React.ReactNode;
}

/**
 * ContractorRoute - Strictly enforces CONTRACTOR role access
 * Only users with role === 'CONTRACTOR' can access routes protected by this component
 * All other users (including admins) are blocked and redirected
 */
const ContractorRoute: React.FC<ContractorRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body2">Loading authentication...</Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const userRole = user.role?.toUpperCase();

  // STRICT: Only CONTRACTOR role can access
  if (userRole !== 'CONTRACTOR') {
    console.warn('[ContractorRoute] Access denied - User role:', userRole, 'Required: CONTRACTOR');
    
    if (userRole === 'ADMIN') {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              ⛔ Access Denied - Contractor Portal Restricted
            </Typography>
            <Typography sx={{ mt: 1 }}>
              You are logged in as an <strong>Administrator</strong>, but this area is restricted to <strong>Contractors only</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Redirecting to your administrator dashboard...
            </Typography>
          </Alert>
          <Navigate to="/dashboard" replace />
        </Box>
      );
    }

    // Unknown role - redirect to login
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ContractorRoute;

